// @flow

import React, { type Node } from 'react';
import { useUniqueId } from '@tectonics/hooks';
import { useUserInputAsTextField } from '@tectonics/prs/user-input-values';
import { InputMask } from '@tectonics/components/input-mask';
import { Label, TextField } from '../../../../../../commons/atoms';
import { InputGroup } from '../../../../../../commons/molecules';
import { ErrorLabel, InputHolder } from '../../commons';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { useUserInputValidationError, type UserInputValidationError, serverErrorMessage } from '@tectonics/prs/validation';
import { useSubmitting } from '@tectonics/prs/submitting';
import { getCountry, useIncomingPayment } from '@tectonics/prs/incoming-payment';
import type { Country } from '@tectonics/prs/country';
import { translate } from '@tectonics/translate';

export function InputCardExpiryDate(): Node {
  const inputId = useUniqueId();
  const submitting = useSubmitting();

  const [cardExpiryDate, handleCardExpiryDateChange] = useUserInputAsTextField('cardExpiryDate');

  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError('cardExpiryDate');
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  return (
    <InputHolder data-test-name="card-expiry-date" style={{ gridColumn: '1/2' }}>
      <InputGroup
        label={
          <Label htmlFor={inputId}>
            <LabelMessage />
          </Label>}
        input={
          <TextField
            as={InputMask}
            id={inputId}
            type="tel"
            value={cardExpiryDate}
            onChange={handleCardExpiryDateChange}
            placeholder={resolvePlaceholderText(country)}
            mask={EXPIRY_DATE_MASK}
            fashion={formHasBeenSent && validationError ? 'error' : null}
            readOnly={submitting}
            data-error={formHasBeenSent && validationError ? 'error' : null}
            data-secret
            name="card-expiry-date"
          />
        }
      />
      {formHasBeenSent && validationError && (
        <ValidationMessage country={country} validationError={validationError} />
      )}
    </InputHolder>
  );
}

function resolvePlaceholderText(country: Country): string {
  switch (country) {
    case 'ar':
    case 'br':
    case 'cl':
    case 'co':
    case 'mx':
    case 'pe':
      return 'MM/AAAA';
    case 'za':
    case 'ke':
    case 'ng':
      return 'MM/YYYY';
    default:
      return 'MM/AA';
  }
}

function LabelMessage(): Node {
  return translate('shopify.inputCardExpiryDate');
}

type ValidationMessageProps = {|
  country: Country,
  validationError: UserInputValidationError,
|};

const messages = (validationError: UserInputValidationError) => {
  switch(validationError.id){
    case 'empty':
      return translate('shopify.errorInsertCardExpiration');

    case 'expired':
      return translate('shopify.errorCardExpired');

    default:
      return translate('shopify.errorInvalidExpirationDate');
  }
};

function ValidationMessage({ validationError }: ValidationMessageProps): Node {
  return (
    <ErrorLabel>
      {messages(validationError) || (serverErrorMessage(validationError.payload))}
    </ErrorLabel>
  );
}

const EXPIRY_DATE_MASK = [
  /[0-9]/, /[0-9]/,
  '/',
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
];
